import { Schema } from 'effect';

import { EmailAddress } from '@finnest/core/effect';

import { HttpUrl } from './_shared/formats.ts';
import { onboardingSubStepIdSchema } from './onboarding/vocabulary/sub-step-id.ts';
import {
  CustomerId,
  InvitationId,
  OwnerId,
  ProviderId,
  ReviewRequestId,
  RunId,
  SignedByOwnerPasskeyId,
} from './onboarding/vocabulary/ids/branded-ids.ts';

/** Where Power serves every onboarding route: under the admin prefix the edge already routes. */
export const ONBOARDING_BASE_PATH = '/power/admin/onboarding';

const Count = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0));

export const onboardingPhaseIds = [
  'p0',
  'p1',
  'p2',
  'p3',
  'p4',
  'p5',
  'p6',
  'p7',
  'p8',
  'p9',
  'p10',
  'p11',
] as const;

export const onboardingPhaseIdSchema = Schema.Literals(onboardingPhaseIds).annotate({
  title: 'Power onboarding phase id',
  description: 'Canonical Power onboarding phase identifier.',
});
export type PhaseId = typeof onboardingPhaseIdSchema.Type;

export const onboardingPhaseLookupSchema = Schema.Union([
  Schema.Struct({ kind: Schema.Literal('phase'), phaseId: onboardingPhaseIdSchema }),
  Schema.Struct({ kind: Schema.Literal('unrecognised'), value: Schema.NonEmptyString }),
]).annotate({
  title: 'Power onboarding phase lookup',
  description: 'Result of mapping a numeric route param onto a canonical onboarding phase id.',
});
export type OnboardingPhaseLookup = typeof onboardingPhaseLookupSchema.Type;

const phaseIdByNumber = new Map(onboardingPhaseIds.map((id) => [id.slice(1), id]));
export const onboardingPhaseIdFromNumber = (value: string): OnboardingPhaseLookup => {
  const mapped = phaseIdByNumber.get(value);
  if (mapped === undefined) return { kind: 'unrecognised', value };
  return { kind: 'phase', phaseId: mapped };
};

export const onboardingSubStepStatusSchema = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal('derived-green'),
    detail: Schema.optionalKey(Schema.NonEmptyString),
    // Warn-level annotation (e.g. "unverified directory record", "demo
    // material") — the sub-step is complete but the UI renders an amber chip.
    warning: Schema.optionalKey(Schema.NonEmptyString),
  }),
  Schema.Struct({ kind: Schema.Literal('action-pending'), hint: Schema.NonEmptyString }),
  Schema.Struct({ kind: Schema.Literal('locked'), prerequisite: onboardingPhaseIdSchema }),
  Schema.Struct({ kind: Schema.Literal('optional'), skipped: Schema.Boolean }),
]).annotate({
  title: 'Power onboarding sub-step status',
  description: 'Derived Power onboarding sub-step status for the admin wizard.',
});
export type SubStepStatus = typeof onboardingSubStepStatusSchema.Type;

export const onboardingPhaseStatusSchema = Schema.Union([
  Schema.Struct({ kind: Schema.Literal('complete') }),
  Schema.Struct({
    kind: Schema.Literal('in-progress'),
    completedSubSteps: Count,
    totalSubSteps: Count,
  }),
  Schema.Struct({
    kind: Schema.Literal('locked'),
    missingPrerequisites: Schema.Array(onboardingPhaseIdSchema),
  }),
]).annotate({
  title: 'Power onboarding phase status',
  description: 'Derived Power onboarding phase status for the admin wizard.',
});
export type PhaseStatus = typeof onboardingPhaseStatusSchema.Type;

export const onboardingSubStepSchema = Schema.Struct({
  id: onboardingSubStepIdSchema,
  title: Schema.NonEmptyString,
  description: Schema.NonEmptyString,
  status: onboardingSubStepStatusSchema,
}).annotate({
  title: 'Power onboarding sub-step',
  description: 'Derived Power onboarding sub-step for the admin wizard.',
});
export type SubStep = typeof onboardingSubStepSchema.Type;

export const onboardingPhaseSchema = Schema.Struct({
  id: onboardingPhaseIdSchema,
  phaseDir: Schema.NonEmptyString,
  title: Schema.NonEmptyString,
  subtitle: Schema.NonEmptyString,
  status: onboardingPhaseStatusSchema,
  subSteps: Schema.Array(onboardingSubStepSchema),
  finnestReviewRequired: Schema.Boolean,
  evidenceRuns: Schema.Array(Schema.Struct({ runId: RunId, startedAt: Schema.NonEmptyString })),
}).annotate({
  title: 'Power onboarding phase',
  description: 'Derived Power onboarding phase status for the admin wizard.',
});
export type Phase = typeof onboardingPhaseSchema.Type;

export const onboardingOverviewSchema = Schema.Struct({
  customerId: CustomerId,
  phases: Schema.Array(onboardingPhaseSchema),
}).annotate({
  title: 'Power onboarding overview',
  description: 'Derived Power onboarding phase overview for one customer.',
});
export type OnboardingOverview = typeof onboardingOverviewSchema.Type;

export const onboardingMfaMethodSchema = Schema.Literals(['passkey', 'totp']);
export type OnboardingMfaMethod = typeof onboardingMfaMethodSchema.Type;

export const onboardingInviteOwnerInputSchema = Schema.Struct({
  customerId: CustomerId,
  email: EmailAddress,
  method: onboardingMfaMethodSchema,
}).annotate({
  title: 'Power onboarding invite Owner input',
  description: 'Internal Power onboarding payload for inviting an Owner during P0.',
});
export type OnboardingInviteOwnerInput = typeof onboardingInviteOwnerInputSchema.Type;

export const onboardingVerifyMfaInputSchema = Schema.Struct({
  customerId: CustomerId,
  ownerId: OwnerId,
  method: onboardingMfaMethodSchema,
}).annotate({
  title: 'Power onboarding verify MFA input',
  description: 'Internal Power onboarding payload for confirming Owner MFA during P0.',
});
export type OnboardingVerifyMfaInput = typeof onboardingVerifyMfaInputSchema.Type;

export const onboardingConfigureSsoInputSchema = Schema.Struct({
  customerId: CustomerId,
  providerId: ProviderId,
  domain: Schema.NonEmptyString,
  metadataUrl: HttpUrl,
}).annotate({
  title: 'Power onboarding configure SSO input',
  description: 'Internal Power onboarding payload for configuring SSO during P0.',
});
export type OnboardingConfigureSsoInput = typeof onboardingConfigureSsoInputSchema.Type;

export const onboardingSkipSsoInputSchema = Schema.Struct({
  customerId: CustomerId,
  signedByOwnerPasskeyId: SignedByOwnerPasskeyId,
}).annotate({
  title: 'Power onboarding skip SSO input',
  description: 'Internal Power onboarding payload for signing the P0 SSO skip attestation.',
});
export type OnboardingSkipSsoInput = typeof onboardingSkipSsoInputSchema.Type;

export const onboardingInviteOwnerResponseSchema = Schema.Struct({
  customerId: CustomerId,
  invitationId: InvitationId,
  email: EmailAddress,
  method: onboardingMfaMethodSchema,
  ownerCountAfter: Count,
}).annotate({
  title: 'Power onboarding invite Owner response',
  description: 'Internal Power onboarding response for P0 Owner invitations.',
});
export type OnboardingInviteOwnerResponse = typeof onboardingInviteOwnerResponseSchema.Type;

export const onboardingSkipSsoResponseSchema = Schema.Struct({
  runId: RunId,
  runPath: Schema.NonEmptyString,
  attestationPath: Schema.NonEmptyString,
}).annotate({
  title: 'Power onboarding skip SSO response',
  description: 'Internal Power onboarding response for P0 SSO skip evidence.',
});
export type OnboardingSkipSsoResponse = typeof onboardingSkipSsoResponseSchema.Type;

// The client sends the phase + evidence-run identifiers it knows from the phase
// overview, not a filesystem path: the service derives the support-bundle path
// under its own evidence root (`evidenceRootFor(runRoot)`). A client-built
// `.finnest/...` path breaks once `FINNEST_RUN_ROOT` points outside the repo, so
// path construction must stay server-side.
export const onboardingReviewRequestInputSchema = Schema.Struct({
  customerId: CustomerId,
  phaseId: onboardingPhaseIdSchema,
  phaseDir: Schema.NonEmptyString,
  runId: RunId,
}).annotate({
  title: 'Power onboarding review request input',
  description: 'Internal Power onboarding payload for requesting Finnest review.',
});
export type OnboardingReviewRequestInput = typeof onboardingReviewRequestInputSchema.Type;

export const onboardingReviewRequestResponseSchema = Schema.Struct({
  reviewRequestId: ReviewRequestId,
}).annotate({
  title: 'Power onboarding review request response',
  description: 'Internal Power onboarding response for Finnest review requests.',
});
export type OnboardingReviewRequestResponse = typeof onboardingReviewRequestResponseSchema.Type;
export * from './onboarding/vocabulary/sub-step-id.ts';
export * from './onboarding/vocabulary/json-value.ts';
export * from './onboarding/phase-p1.ts';
export * from './onboarding/p1/phase-p1-provider-as.ts';
export * from './onboarding/p1/phase-p1-counterparty-contacts.ts';
export * from './onboarding/p1/phase-p1-cert-artifact.ts';
export * from './onboarding/phase-p2.ts';
export * from './onboarding/phase-p3.ts';
export * from './onboarding/phase-p4.ts';
export * from './onboarding/p5/phase-p5.ts';
export * from './onboarding/p6/phase-p6.ts';
export * from './onboarding/p7/phase-p7.ts';
export * from './onboarding/p8/phase-p8.ts';
export * from './onboarding/p9/phase-p9.ts';
export * from './onboarding/p9/phase-p9-signoff.ts';
export * from './onboarding/p10/phase-p10.ts';
export * from './onboarding/p10/phase-p10-drill.ts';
export * from './onboarding/p11/phase-p11.ts';
export * from './onboarding/p11/phase-p11-identity.ts';
